import PropTypes from 'prop-types';
import LineChart from '@/components/charts/LineChart';
import { numberFormat } from '@/utils/unitUtils';

/**
 * Renders a run_sensitivity_sweep result as a line chart.
 *
 * The `data` prop is the raw tool result:
 *   { param_name, analysis_type, unit, points: [{param_value, result_value}] }
 *
 * Multiple datasets (e.g. two pathways for breakeven) can be passed as an
 * array of sweep results via the `datasets` prop. When `datasets` is provided
 * it takes precedence over `data`.
 */
function SweepResultCard({ data, datasets }) {
  // Normalise: datasets prop overrides single data, used for breakeven plots.
  const sweepSeries = datasets ?? (data ? [data] : []);

  if (sweepSeries.length === 0 || sweepSeries[0]?.error) {
    return (
      <div className="p-4 text-sm text-red-500">
        {sweepSeries[0]?.error ?? 'No sweep data available.'}
      </div>
    );
  }

  const first = sweepSeries[0];
  const { param_name, analysis_type, unit, target_value, breakeven_param_value } = first;
  const paramLabel = (param_name ?? '').replace(/_/g, ' ');

  // Build the chart data structure expected by LineChart.
  // All series share the same x-axis labels (param values from the first series).
  const labels = first.points?.map(p => p.param_value) ?? [];

  const chartResults = {
    labels,
    datasets: sweepSeries.map(s => ({
      label: s.pathway_name ?? s.case_id ?? unit,
      data: s.points?.map(p => p.result_value) ?? [],
    })),
  };

  const resultLabel = analysis_type === 'lca' ? `Carbon intensity (${unit})` : `Levelized cost (${unit})`;

  return (
    <div className="p-5 space-y-4">
      <div>
        <div className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-0.5">
          Parameter sweep
        </div>
        <div className="text-base font-semibold text-slate-800 capitalize">{paramLabel}</div>
        <div className="text-xs text-slate-500 mt-0.5">
          {labels.length} points · {analysis_type?.toUpperCase()}
          {breakeven_param_value != null && (
            <span className="ml-2 text-slate-600">
              · Breakeven at <span className="font-medium tabular-nums">{numberFormat(breakeven_param_value)}</span>
            </span>
          )}
        </div>
      </div>

      <LineChart
        results={chartResults}
        label={paramLabel}
        yAxisLabel={resultLabel}
        legendTitle={sweepSeries.length > 1 ? 'Pathway' : undefined}
        targetValue={target_value}
      />
    </div>
  );
}

SweepResultCard.propTypes = {
  data: PropTypes.shape({
    param_name: PropTypes.string,
    analysis_type: PropTypes.string,
    unit: PropTypes.string,
    case_id: PropTypes.string,
    pathway_name: PropTypes.string,
    points: PropTypes.arrayOf(PropTypes.shape({
      param_value: PropTypes.number,
      result_value: PropTypes.number,
    })),
    error: PropTypes.string,
  }),
  datasets: PropTypes.arrayOf(PropTypes.object),
};

export default SweepResultCard;
